/**
 * Button — the canonical button primitive.
 *
 * Mirrors the ArmorIQ Foundations Figma Button component set:
 *   variant: primary | secondary | tertiary | ghost | danger | dangerSoft
 *   size:    sm | md         (the variant matrix is intentionally smaller in
 *                             Figma — sm/md cover every use site today; xs is
 *                             a code-only convenience for tight chrome rows)
 *
 * Boolean knobs the design system needs but the Figma matrix doesn't multiply
 * (kept as React-level props so they stay cheap):
 *   iconOnly: turns the button into a square; label becomes the aria-label
 *   loading:  swaps the leading slot for a spinner, blocks interaction
 *   leading / trailing: slot props for an icon component (default: undefined)
 *
 * Replaces 367 ad-hoc `<button>` sites + Modal's bespoke
 * `ModalPrimaryButton` / `ModalSecondaryButton`. Both of those keep working
 * as re-exports for now so existing Modal callers don't churn; the migration
 * sweep folds them in later.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ComponentType,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { IconCheck, type IconProps } from '@shared/icons';
import { Spinner } from '../Spinner';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'ghost'
  | 'danger'
  | 'dangerSoft';

export type ButtonSize = 'xs' | 'sm' | 'md';

/** The lifecycle an `onAction` button moves through. Exported for tests. */
export type ActionState = 'idle' | 'pending' | 'settled' | 'failed';

/** How long the settled confirmation dwells before returning to idle. */
const DEFAULT_SETTLE_MS = 1400;

export type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Hides the label, makes the button square. `children` becomes aria-label. */
  iconOnly?: boolean;
  /** Replaces the leading slot with a spinner; blocks interaction. */
  loading?: boolean;
  leading?: ComponentType<IconProps>;
  trailing?: ComponentType<IconProps>;
  /** Visible label. With iconOnly, this is read as aria-label only. */
  children?: ReactNode;
  /** Async handler. The button derives its own pending state from the returned promise. */
  onAction?: (event: MouseEvent<HTMLButtonElement>) => unknown;
  /** Label shown while `onAction` is in flight. Defaults to `children`. */
  pendingLabel?: ReactNode;
  /** Called when `onAction` rejects or throws. Render the reason next to the control. */
  onActionError?: (error: unknown) => void;
  /** Dwell time on the success confirmation, in ms. 0 skips it. */
  settleMs?: number;
};

const VARIANT_CLS: Record<ButtonVariant, string> = {
  primary: 'bg-aq-accent text-aq-ink-on hover:bg-aq-accent-hover active:bg-aq-accent-active',
  secondary:
    'bg-aq-surface text-aq-ink-soft border-aq-border border hover:bg-aq-hover-wash active:bg-aq-hover-wash-strong',
  tertiary: 'bg-transparent text-aq-accent-strong hover:bg-aq-accent-soft',
  ghost: 'bg-transparent text-aq-ink-soft hover:bg-aq-hover-wash active:bg-aq-hover-wash-strong',
  danger: 'bg-aq-bad text-aq-ink-on hover:bg-aq-bad-strong active:bg-aq-bad-strong',
  dangerSoft:
    'bg-aq-bad-soft text-aq-bad border-aq-bad/30 border hover:bg-aq-bad-soft-strong active:bg-aq-bad-soft-strong',
};

/** Pending fill per variant. Solid variants darken; outline and text keep their surface. */
const PENDING_CLS: Record<ButtonVariant, string> = {
  primary: 'bg-aq-accent-pending hover:bg-aq-accent-pending',
  secondary: 'bg-aq-surface hover:bg-aq-surface',
  tertiary: 'bg-aq-accent-soft hover:bg-aq-accent-soft',
  ghost: 'bg-aq-hover-wash hover:bg-aq-hover-wash',
  danger: 'bg-aq-bad-pending hover:bg-aq-bad-pending',
  dangerSoft: 'bg-aq-bad-soft-strong hover:bg-aq-bad-soft-strong',
};

const SIZE_CLS: Record<ButtonSize, string> = {
  xs: 'h-7 px-2.5 text-aq-caption gap-1',
  sm: 'h-8 px-3 text-aq-xs gap-1.5',
  md: 'h-10 px-3.5 text-aq-sm gap-1.5',
};

const ICON_SIZE: Record<ButtonSize, number> = { xs: 11, sm: 12, md: 13 };

const ICON_ONLY_PAD: Record<ButtonSize, string> = {
  xs: 'h-7 w-7 p-0',
  sm: 'h-8 w-8 p-0',
  md: 'h-10 w-10 p-0',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    iconOnly = false,
    loading = false,
    leading: LeadingIcon,
    trailing: TrailingIcon,
    disabled,
    className,
    children,
    type = 'button',
    onAction,
    onClick,
    pendingLabel,
    onActionError,
    settleMs = DEFAULT_SETTLE_MS,
    ...rest
  },
  ref
) {
  const [actionState, setActionState] = useState<ActionState>('idle');

  // `alive` guards a resolution after unmount; `runId` guards a stale one after a newer press.
  const alive = useRef(true);
  const runId = useRef(0);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (settleTimer.current !== undefined) clearTimeout(settleTimer.current);
    };
  }, []);

  const isPending = loading || actionState === 'pending';

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      // aria-disabled leaves the element clickable, so pending is swallowed here.
      if (disabled || isPending) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      onClick?.(event);
      if (!onAction) return;

      const id = runId.current + 1;
      runId.current = id;
      if (settleTimer.current !== undefined) clearTimeout(settleTimer.current);
      setActionState('pending');

      // An async IIFE so a synchronous throw is caught like a rejection.
      void (async () => {
        try {
          await onAction(event);
          if (!alive.current || runId.current !== id) return;
          if (settleMs <= 0) {
            setActionState('idle');
            return;
          }
          setActionState('settled');
          settleTimer.current = setTimeout(() => {
            if (alive.current && runId.current === id) setActionState('idle');
          }, settleMs);
        } catch (error) {
          // A newer press owns the button; that run reports its own outcome.
          if (runId.current !== id) return;
          // Reported before the mount check: an optimistic update can unmount
          // the button before its own request fails.
          onActionError?.(error);
          if (!alive.current) return;
          setActionState('failed');
          if (settleMs > 0) {
            settleTimer.current = setTimeout(() => {
              if (alive.current && runId.current === id) setActionState('idle');
            }, settleMs);
          }
        }
      })();
    },
    [disabled, isPending, onAction, onActionError, onClick, settleMs]
  );

  const showSettled = actionState === 'settled';
  const iconPx = ICON_SIZE[size];

  // Every slot stays reserved in every state so the width never changes.
  const canSettle = onAction !== undefined && settleMs > 0;
  const reservesLeadingSlot = canSettle || onAction !== undefined || loading;

  const blocked = Boolean(disabled) || isPending;
  const label = isPending && pendingLabel !== undefined ? pendingLabel : children;

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      // Exposes the lifecycle; settled and failed have no ARIA equivalent.
      data-action-state={onAction ? actionState : undefined}
      aria-disabled={blocked || undefined}
      aria-busy={isPending || undefined}
      aria-label={iconOnly && typeof children === 'string' ? children : rest['aria-label']}
      onClick={handleClick}
      className={[
        // `transition` (not just `-colors`) so the press scale tweens too; the
        // tactile dip on press is what makes every button across the app feel
        // alive. motion-safe-gated and disabled when the button is blocked.
        // `cursor-pointer` is set here rather than relying solely on the
        // base-layer rule in globals.css: this is the most-used interactive
        // element in the app, and it should not lose its hand cursor because a
        // selector elsewhere in a shared rule failed to parse.
        'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md font-semibold transition duration-150 ease-out',
        // Opacity is disabled-only; pending darkens its fill instead.
        'disabled:cursor-not-allowed disabled:opacity-60',
        isPending ? 'cursor-progress' : '',
        !blocked ? 'motion-safe:active:scale-[0.97]' : '',
        VARIANT_CLS[variant],
        isPending ? PENDING_CLS[variant] : '',
        iconOnly ? ICON_ONLY_PAD[size] : SIZE_CLS[size],
        className ?? '',
      ].join(' ')}
      {...rest}
    >
      {/* Leading slot. ONE glyph position carries the whole lifecycle: the
          leading icon at rest, the spinner while pending, the tick on settle.
          The slot is the same width in every state, so nothing moves; what
          changes is which glyph occupies it. Reserved with a transparent
          placeholder on buttons that have no leading icon but can still show a
          spinner or a tick, so those do not widen mid-cycle either. */}
      {isPending ? (
        <Spinner size={iconPx} />
      ) : showSettled ? (
        <IconCheck size={iconPx} stroke={2.2} />
      ) : LeadingIcon ? (
        <LeadingIcon size={iconPx} stroke={1.8} />
      ) : reservesLeadingSlot ? (
        <span aria-hidden="true" style={{ width: iconPx, height: iconPx }} />
      ) : null}

      {/* Label stack. Both strings occupy the same grid cell, the inactive one
          hidden but still laid out, so the button is always as wide as its
          WIDEST state. Without this the swap to a longer gerund moved the
          button by up to 72% mid-request, reflowing every sibling in a
          toolbar while the cursor was still over it. */}
      {!iconOnly ? (
        <span className="grid">
          <span className="invisible col-start-1 row-start-1 truncate" aria-hidden="true">
            {children}
          </span>
          {pendingLabel !== undefined ? (
            <span className="invisible col-start-1 row-start-1 truncate" aria-hidden="true">
              {pendingLabel}
            </span>
          ) : null}
          <span className="col-start-1 row-start-1 truncate">{label}</span>
        </span>
      ) : null}

      {/* Trailing slot: only ever the caller's trailing icon now that the
          spinner lives in the leading position. Still reserved so a trailing
          icon hidden during settle does not change the width. */}
      {!iconOnly && TrailingIcon ? (
        showSettled || isPending ? (
          <span aria-hidden="true" style={{ width: iconPx, height: iconPx }} />
        ) : (
          <TrailingIcon size={iconPx} stroke={1.8} />
        )
      ) : null}
    </button>
  );
});

export default Button;
